import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { CircleHelpIcon } from 'lucide-react';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';

type TooltipComponentProps = {
  className?: string;
  contentClassName?: string;
  followCursor?: boolean;
  side?: 'bottom' | 'left' | 'right' | 'top';
  triggerClassName?: string;
  trigger?: React.ReactNode;
};

export const TooltipComponent: React.FC<PropsWithChildren<TooltipComponentProps>> = (props) => {
  const {
    children,
    className,
    contentClassName,
    triggerClassName,
    trigger,
    side = 'top',
    followCursor = false,
  } = props;
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!followCursor) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (isVisible) {
        setMousePosition({ x: event.clientX, y: event.clientY });
      }
    };

    const handleMouseEnter = (event: MouseEvent) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
      setIsVisible(true);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    const triggerElement = triggerRef.current;
    if (triggerElement) {
      triggerElement.addEventListener('mouseenter', handleMouseEnter);
      triggerElement.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (triggerElement) {
        triggerElement.removeEventListener('mouseenter', handleMouseEnter);
        triggerElement.removeEventListener('mouseleave', handleMouseLeave);
      }
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [followCursor, isVisible]);

  if (followCursor) {
    return (
      <>
        <div ref={triggerRef}>
          {trigger || (
            <span
              className={cn(
                'flex size-4 cursor-help items-center justify-center text-gray-400 hover:text-gray-600',
                triggerClassName,
              )}
            >
              <CircleHelpIcon className="size-4" />
            </span>
          )}
        </div>
        {isVisible && (
          <div
            className={cn(
              'fixed z-9999 pointer-events-none bg-white border border-gray-200 rounded-md max-w-[400px] text-xs text-stone-900/70 shadow-md overflow-y-auto',
              className,
            )}
            style={{
              left: mousePosition.x + 30,
              top: mousePosition.y - 20,
            }}
          >
            <div className="px-3 py-1.5 whitespace-pre-wrap">{children}</div>
          </div>
        )}
      </>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          {trigger || (
            <span
              className={cn(
                'flex size-4 cursor-help items-center justify-center text-gray-400 hover:text-gray-600',
                triggerClassName,
              )}
            >
              <CircleHelpIcon className="size-4" />
            </span>
          )}
        </TooltipTrigger>
        {children && (
          <TooltipContent
            className={cn('p-0', contentClassName)}
            side={side}
          >
            <div
              className={cn(
                'text-xs text-stone-900/70 z-50 max-w-[600px] text-wrap whitespace-pre-line overflow-y-auto',
                className,
              )}
            >
              <div className="px-3 py-1.5 whitespace-pre-wrap">{children}</div>
            </div>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};
